import { ILandingRestaurantList, Restaurant } from '../types'

/**
 * Maps restaurants with their corresponding predictions by index.
 * Predictions are attached as optional metadata to restaurants.
 *
 * @param list - The landing restaurant list containing restaurants and optional predictions
 * @returns Array of restaurants with optional predictionYes percentage and recommendation summary
 */
export function mapRestaurantsWithPredictions(
  list: ILandingRestaurantList,
): Array<Restaurant & { predictionYes?: number; recommendationSummary?: string }> {
  const { restaurants, predictedRestaurants } = list

  if (!predictedRestaurants || predictedRestaurants.length === 0) {
    return restaurants
  }

  return restaurants.map((restaurant, index) => {
    const prediction = predictedRestaurants[index]
    const predictionYes = prediction?.recommendation?.yes
    const recommendationSummary = prediction?.recommendation?.summary

    return {
      ...restaurant,
      predictionYes:
        typeof predictionYes === 'number' ? predictionYes : undefined,
      recommendationSummary:
        typeof recommendationSummary === 'string' ? recommendationSummary : undefined,
    }
  })
}

/**
 * Match-quality band thresholds — the SINGLE SOURCE OF TRUTH for deriving a match band from the
 * per-restaurant match percentage on the frontend.
 *
 * The backend now sends a CALIBRATED match % on the full 0-100 display scale (the like model's raw
 * "yes" score is compressed into ~[0, 34], so `RestaurantService.calibrateMatchScore` stretches it
 * onto 0-100 via an empirical percentile curve derived from a production score sample). These
 * thresholds MIRROR the backend calibrated bands `RestaurantService.computeMatchLabel` applies
 * (CAL_STRONG=80, CAL_GOOD=55, CAL_OKAY=30). The percentage arrives already calibrated — do NOT
 * transform it again here. Keep these in lockstep with the backend calibrated thresholds.
 */
export const MATCH_STRONG_THRESHOLD = 80
export const MATCH_GOOD_THRESHOLD = 55
export const MATCH_OKAY_THRESHOLD = 30

export type MatchBand = 'strong' | 'good' | 'okay' | 'low' | 'unknown'

/**
 * Classifies a numeric like-model "yes" score (0-100) into one of the backend-aligned match bands.
 * The one place FE match cutoffs live — everything else derives from this.
 *
 * A null/undefined/NaN percentage means the restaurant is UNSCORED (no scoreable dishes): it maps
 * to the neutral 'unknown' band, NEVER to 'low'. An unscored restaurant is unknown, not a 0% match.
 *
 * @param percentage - The prediction percentage (0-100), or null/undefined when unscored
 */
export function getMatchBand(
  percentage: number | null | undefined,
): MatchBand {
  if (percentage === null || percentage === undefined || Number.isNaN(percentage)) {
    return 'unknown'
  }
  if (percentage >= MATCH_STRONG_THRESHOLD) {
    return 'strong'
  } else if (percentage >= MATCH_GOOD_THRESHOLD) {
    return 'good'
  } else if (percentage >= MATCH_OKAY_THRESHOLD) {
    return 'okay'
  } else {
    return 'low'
  }
}

const MATCH_BAND_TEXT: Record<MatchBand, string> = {
  strong: 'Strong match',
  good: 'Good match',
  okay: 'Okay match',
  low: 'Low match',
  // Unscored -> render nothing (neutral). Never surface a literal "Unknown" pill.
  unknown: '',
}

const MATCH_BAND_CATEGORY: Record<MatchBand, string> = {
  strong: 'perfectmatch',
  good: 'greatmatch',
  okay: 'okmatch',
  low: 'lowmatch',
  unknown: 'unknown',
}

/**
 * Converts a percentage prediction to a human-readable match quality text, using the shared bands
 * so it stays consistent with the backend `matchLabel` vocabulary.
 *
 * @param percentage - The prediction percentage (0-100)
 * @returns Human-readable match quality text
 */
export function getMatchQualityText(percentage: number | null | undefined): string {
  return MATCH_BAND_TEXT[getMatchBand(percentage)]
}

/**
 * Gets the match quality category key (used by the match-quality filter dropdowns) from the shared
 * bands, so all filter sites classify identically and consistently with the backend.
 *
 * @param percentage - The prediction percentage (0-100)
 * @returns Match quality category key
 */
export function getMatchQualityCategory(percentage: number | null | undefined): string {
  return MATCH_BAND_CATEGORY[getMatchBand(percentage)]
}

/**
 * True when a backend `matchLabel` represents an UNSCORED / neutral restaurant that must NOT render
 * a colored match pill. Covers null/empty and the backend `UNKNOWN_MATCH_LABEL` ("Unknown"), with or
 * without a trailing " match", case-insensitively. Use this to hide the pill (or render a neutral
 * chip) instead of ever showing a literal "Unknown" / "Unknown match" pill.
 *
 * @param label - The backend match label (may be null/undefined)
 */
export function isNeutralMatchLabel(label: string | null | undefined): boolean {
  if (label === null || label === undefined) {
    return true
  }
  const normalized = label.trim().toLowerCase().replace(/\s+match$/, '')
  return normalized === '' || normalized === 'unknown'
}





